// The in-app alert: a short chime plus a vibration, for when something arrives while the app is
// actually open. (Push covers the app being closed; this covers the doctor holding the phone.)
//
// The tone is synthesised with WebAudio rather than shipped as an mp3 — two sine notes, ~0.4s, no
// asset to download, no cache to bust, and it can be re-pitched per alert kind.
//
// Autoplay policy: a browser will not let a page make noise before the user has interacted with it.
// So the AudioContext is created lazily and unlocked on the first real gesture; until then the
// vibration still fires and the toast still shows.

const MUTE_KEY = 'av:alerts-muted';

let ctx: AudioContext | null = null;
let unlocked = false;

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Call once from a click/tap anywhere in the app. Creating and resuming the context inside a real
 * gesture is what buys permission to play sound later, when the SSE event arrives on its own.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  const a = audio();
  if (!a) return;
  void a.resume().then(() => {
    unlocked = true;
  });
}

export function alertsMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setAlertsMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* storage blocked — the setting just won't persist */
  }
}

/** Two-note chime. `urgent` raises the pitch and adds a third note, for approvals. */
function chime(urgent: boolean): void {
  const a = audio();
  if (!a || a.state === 'suspended') return;
  const now = a.currentTime;
  const notes = urgent ? [880, 1108.7, 1318.5] : [659.3, 880];
  notes.forEach((freq, i) => {
    const osc = a.createOscillator();
    const gain = a.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // Quick attack, gentle decay — a "ping", not a beep.
    const start = now + i * 0.12;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.34);
    osc.connect(gain).connect(a.destination);
    osc.start(start);
    osc.stop(start + 0.36);
  });
}

function vibrate(urgent: boolean): void {
  if (!('vibrate' in navigator)) return;
  try {
    navigator.vibrate(urgent ? [120, 60, 120, 60, 180] : [120, 60, 120]);
  } catch {
    /* some browsers throw when the page is not visible */
  }
}

/**
 * Ring. Silent when muted, and never for an event the user caused themselves — callers are
 * responsible for not alerting on their own actions.
 */
export function ring(kind: 'message' | 'approval' | 'update' = 'message'): void {
  if (alertsMuted()) return;
  const urgent = kind === 'approval';
  chime(urgent);
  vibrate(urgent);
}
